import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { excursionsService } from '@/api/services';
import {
  getAvailableDatesSet,
  getSchedulesForDate,
  formatYMD,
  normalizeSalesCapacityRow,
  scheduleHasPickupAvailable,
  buildDayCapacityStatusMap,
} from '@/utils/excursionAvailability';
import { getNextAvailableExcursionDateLabel } from '@/utils/excursionNextAvailableDate';
import { optionMatchesLanguage } from '@/utils/excursionLanguageFilter';

function parseBookingFilterQuery(params) {
  const dateStr = params.get('date') || params.get('dateFrom') || '';
  const adultsRaw = Number(params.get('adults') ?? 2);
  const childrenRaw = Number(params.get('children') ?? 0);
  const infantsRaw = Number(params.get('infants') ?? 0);
  const lang = params.get('lang') || params.get('language') || null;
  const adults = Number.isFinite(adultsRaw) && adultsRaw >= 1 ? adultsRaw : 2;
  const children = Number.isFinite(childrenRaw) && childrenRaw >= 0 ? childrenRaw : 0;
  const infants = Number.isFinite(infantsRaw) && infantsRaw >= 0 ? infantsRaw : 0;
  let parsedDate = null;
  if (dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (y && m && d) parsedDate = new Date(y, m - 1, d);
  }
  return {
    dateStr,
    parsedDate,
    adults,
    children,
    infants,
    lang,
    scheduleId: Number(params.get('schedule') || 0) || null,
    pickupRegion: Number(params.get('pickup_region') || 0) || null,
    pickupName: params.get('pickup_name') || null,
    pickupTime: params.get('pickup_time') || null,
    key: `${dateStr}|${adults}|${children}|${infants}|${lang || ''}`,
  };
}

function bookingFilterQueryKey(dateYmd, passengers, language) {
  return [
    dateYmd || '',
    String(Number(passengers?.adults || 0)),
    String(Number(passengers?.children || 0)),
    String(Number(passengers?.infants || 0)),
    language || '',
  ].join('|');
}

/**
 * Booking search state, URL sync, pricelist options, pickup grouping, and region pricing
 * for the excursion detail hero (main column + sidebar).
 */
export default function useExcursionDetailBooking({
  excursion,
  pickupsData,
  partnerAttribution,
  searchParams,
}) {
  const [bookingFormData, setBookingFormData] = useState({
    passengers: { adults: 2, children: 0, infants: 0 },
    date: null,
    language: null,
  });
  const [availabilitySought, setAvailabilitySought] = useState(false);
  const [bookingOptions, setBookingOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedPickupBySchedule, setSelectedPickupBySchedule] = useState({});
  const [regionPriceCache, setRegionPriceCache] = useState({});
  const [openPickupScheduleId, setOpenPickupScheduleId] = useState(null);

  const hasDateAndPax = bookingFormData.date && (bookingFormData.passengers?.adults ?? 0) >= 1;
  const showOptions = hasDateAndPax && availabilitySought;
  const didInitFromQueryRef = useRef(false);
  const queryProvidedDateRef = useRef(false);
  const lastAppliedFilterQueryRef = useRef('');
  const lastSeenNextFilterKeyRef = useRef('');
  const pendingPickupRef = useRef(null);
  const lastSyncedQueryRef = useRef('');
  const optionsContextKeyRef = useRef('');
  const [capacityStatusByYmd, setCapacityStatusByYmd] = useState(() => new Map());
  const [nextAvailableDateSuggestion, setNextAvailableDateSuggestion] = useState(null);

  const excId = excursion?.id;

  const availableDatesSet = useMemo(
    () => getAvailableDatesSet(excursion?.schedules ?? [], { monthsAhead: 12 }),
    [excursion?.schedules],
  );

  useEffect(() => {
    const nextParsed = parseBookingFilterQuery(searchParams);

    const applyParsed = (parsed) => {
      lastAppliedFilterQueryRef.current = parsed.key;
      if (parsed.parsedDate) queryProvidedDateRef.current = true;
      setBookingFormData((prev) => ({
        ...prev,
        date: parsed.parsedDate || prev.date,
        passengers: {
          adults: parsed.adults,
          children: parsed.children,
          infants: parsed.infants,
        },
        language: parsed.lang,
      }));
      if (parsed.scheduleId) setSelectedOption(parsed.scheduleId);
      if (parsed.parsedDate) setAvailabilitySought(true);
      if (parsed.scheduleId && (parsed.pickupRegion || parsed.pickupName || parsed.pickupTime)) {
        pendingPickupRef.current = {
          scheduleId: parsed.scheduleId,
          pickupRegion: parsed.pickupRegion,
          pickupName: parsed.pickupName,
          pickupTime: parsed.pickupTime,
        };
      }
    };

    if (!didInitFromQueryRef.current) {
      // Prefer the address bar on first paint — replaceState can be ahead of Next.
      const source = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : searchParams;
      didInitFromQueryRef.current = true;
      lastSeenNextFilterKeyRef.current = nextParsed.key;
      applyParsed(parseBookingFilterQuery(source));
      return;
    }

    // Ignore Next identity churn. Only apply when the router query actually changed.
    if (nextParsed.key === lastSeenNextFilterKeyRef.current) return;
    lastSeenNextFilterKeyRef.current = nextParsed.key;
    if (nextParsed.key === lastAppliedFilterQueryRef.current) return;
    applyParsed(nextParsed);
  }, [searchParams]);

  useEffect(() => {
    if (!didInitFromQueryRef.current || queryProvidedDateRef.current) return;
    if (!excursion?.schedules?.length || availableDatesSet.size === 0 || bookingFormData.date != null) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sorted = Array.from(availableDatesSet).sort();
    const first = sorted.find((ymd) => ymd >= formatYMD(today));
    if (first) {
      const [y, m, d] = first.split('-').map(Number);
      setBookingFormData((prev) => ({ ...prev, date: new Date(y, m - 1, d) }));
    }
  }, [excursion?.schedules?.length, availableDatesSet, bookingFormData.date]);

  useEffect(() => {
    if (!showOptions || !excId || !excursion?.schedules?.length) {
      setBookingOptions([]);
      setSelectedOption(null);
      setSelectedPickupBySchedule({});
      setOpenPickupScheduleId(null);
      setOptionsError(null);
      optionsContextKeyRef.current = '';
      return;
    }
    const tripDateYmd = formatYMD(bookingFormData.date);
    const contextKey = `${tripDateYmd}|${String(bookingFormData.language || '').toLowerCase()}`;
    const contextChanged = optionsContextKeyRef.current !== contextKey;
    optionsContextKeyRef.current = contextKey;
    if (contextChanged) {
      setSelectedPickupBySchedule({});
      setOpenPickupScheduleId(null);
    }
    const year = bookingFormData.date.getFullYear();
    setLoadingOptions(true);
    setOptionsError(null);
    Promise.all([
      excursionsService.getPricelists(year, excId),
      excursionsService.getSalesCapacity({
        date_from: tripDateYmd,
        date_to: tripDateYmd,
        exc_id: Number(excId || 0),
      }).catch(() => ({ data: { data: [] } })),
    ])
      .then(([pricingRes, capacityRes]) => {
        const list = pricingRes.data?.data ?? pricingRes.data ?? [];
        const priceByScheduleId = {};
        list.forEach((row) => {
          priceByScheduleId[row.schedule_id] = {
            ...row,
            price: row.final_price ?? row.price,
            child_price: row.final_child_price ?? row.child_price,
            infant_price: row.final_infant_price ?? row.infant_price,
          };
        });
        const capacityRows = Array.isArray(capacityRes?.data?.data) ? capacityRes.data.data : [];
        const capacityByScheduleId = new Map();
        capacityRows.forEach((row) => {
          const scheduleId = Number(row?.schedule_id || 0);
          if (!scheduleId) return;
          const normalized = normalizeSalesCapacityRow(row, tripDateYmd);
          if (normalized) capacityByScheduleId.set(scheduleId, normalized);
        });
        const dayStatus = buildDayCapacityStatusMap(
          capacityRows,
          excursion.schedules,
          { languageCode: bookingFormData.language },
        );
        if (dayStatus.size > 0) {
          setCapacityStatusByYmd((prev) => {
            const next = new Map(prev);
            dayStatus.forEach((status, ymd) => next.set(ymd, status));
            return next;
          });
        }
        const schedulesOnDate = getSchedulesForDate(excursion.schedules, bookingFormData.date);
        const requestedSeats =
          Math.max(0, Number(bookingFormData.passengers?.adults) || 0)
          + Math.max(0, Number(bookingFormData.passengers?.children) || 0);
        const options = schedulesOnDate
          .map((schedule) => {
            const priceRow = priceByScheduleId[schedule.id];
            const hasLanguage = schedule.language_id != null && schedule.language_id !== '' && (schedule.language_name || schedule.language_code);
            const minimumAdults = Math.max(1, Number(schedule?.minimum_adults) || 1);
            const scheduleAwarePriceRow = priceRow
              ? { ...priceRow, minimum_adults: minimumAdults, schedule }
              : { minimum_adults: minimumAdults, schedule };
            const capacity = capacityByScheduleId.get(Number(schedule.id)) || null;
            const remainingSeats = capacity?.remainingSeats ?? null;
            const insufficientForParty =
              remainingSeats != null && requestedSeats > 0 && requestedSeats > remainingSeats;
            return {
              scheduleId: schedule.id,
              scheduleName: schedule.name,
              languageName: hasLanguage ? (schedule.language_name || schedule.name || 'Tour') : null,
              languageCode: hasLanguage ? schedule.language_code : null,
              isGuidedTour: schedule.is_guided_tour === 1 || schedule.is_guided_tour === true || schedule.is_guided_tour === '1',
              schedule,
              basePriceRow: scheduleAwarePriceRow,
              priceRow: scheduleAwarePriceRow,
              capacity,
              insufficientForParty,
            };
          })
          .filter((opt) => {
            return optionMatchesLanguage(opt, bookingFormData.language);
          })
          .filter((opt) => opt.priceRow && (opt.priceRow.price != null || opt.priceRow.price === 0))
          .filter((opt) => !(opt.capacity?.blocked));
        setBookingOptions(options);
        setSelectedOption((prev) => (options.some((o) => o.scheduleId === prev) ? prev : null));
        setSelectedPickupBySchedule((prev) => {
          const next = {};
          Object.entries(prev || {}).forEach(([sid, pickup]) => {
            if (options.some((o) => String(o.scheduleId) === String(sid))) {
              next[sid] = pickup;
            }
          });
          return next;
        });
      })
      .catch((err) => {
        setOptionsError(err.response?.data?.message || err.message || 'Failed to load options');
        setBookingOptions([]);
      })
      .finally(() => setLoadingOptions(false));
  }, [showOptions, excId, bookingFormData.date, bookingFormData.language, bookingFormData.passengers, excursion?.schedules]);

  const getRegionName = useCallback((pickup) =>
    pickup?.accommodation?.area?.name
      ?? pickup?.accommodation?.region_name
      ?? pickup?.accommodation?.area_name
      ?? pickup?.region_name
      ?? pickup?.region?.name
      ?? 'Other'
  , []);

  const getPickupRegionId = useCallback((pickup) => (
    Number(
      pickup?.region_id
      ?? pickup?.region?.id
      ?? pickup?.accommodation?.area?.id
      ?? pickup?.accommodation?.region_id
      ?? pickup?.acc?.area?.id
      ?? pickup?.acc?.region_id
      ?? 0
    ) || 0
  ), []);

  const getPickupRegionOrder = useCallback((pickup) => {
    const orderRaw =
      pickup?.region_order
      ?? pickup?.region?.display_order
      ?? pickup?.region?.order
      ?? pickup?.accommodation?.area?.display_order
      ?? pickup?.accommodation?.area?.order
      ?? pickup?.acc?.area?.display_order
      ?? pickup?.acc?.area?.order
      ?? 9999;
    const order = Number(orderRaw);
    return Number.isFinite(order) ? order : 9999;
  }, []);

  const getPickupGroupsForSchedule = useCallback((scheduleId) => {
    const schedule = (excursion?.schedules || []).find((s) => Number(s.id) === Number(scheduleId));
    if (schedule && !scheduleHasPickupAvailable(schedule)) return [];

    const byRegion = new Map();
    const seen = new Set();
    pickupsData
      .filter((p) => Number(p?.schedule_id ?? p?.schedule?.id) === Number(scheduleId))
      .forEach((p) => {
        const regionName = getRegionName(p) || 'Other';
        const accommodationName = p?.accommodation?.name ?? p?.acc?.name ?? 'Hotel';
        const accId = Number(
          p?.acc_id
          ?? p?.accommodation_id
          ?? p?.hotel_id
          ?? p?.accommodation?.id
          ?? p?.acc?.id
          ?? 0
        ) || 0;
        const pickupTime = p?.pickup_time ?? '';
        const regionId = getPickupRegionId(p);
        const regionOrder = getPickupRegionOrder(p);
        const key = `${regionName}|${accommodationName}|${pickupTime}|${regionId}|${accId}`;
        if (seen.has(key)) return;
        seen.add(key);
        if (!byRegion.has(regionName)) {
          byRegion.set(regionName, { regionName, regionId, regionOrder, pickups: [] });
        }
        const group = byRegion.get(regionName);
        if (!group.regionId && regionId) group.regionId = regionId;
        group.regionOrder = Math.min(Number(group.regionOrder ?? 9999), Number(regionOrder ?? 9999));
        group.pickups.push({
          accommodationName,
          accId,
          pickupTime,
          regionId,
          regionOrder,
          scheduleId: Number(scheduleId) || 0,
          regionName,
          feeder_required: p?.feeder_required,
          feeder_departure_time: p?.feeder_departure_time,
          feeder_arrival_time: p?.feeder_arrival_time,
          feeder_pickup_location: p?.feeder_pickup_location,
          feeder_meeting_point: p?.feeder_meeting_point,
        });
      });
    return Array.from(byRegion.values()).sort(
      (a, b) => (Number(a.regionOrder ?? 9999) - Number(b.regionOrder ?? 9999))
        || String(a.regionName || '').localeCompare(String(b.regionName || '')),
    );
  }, [excursion?.schedules, getPickupRegionId, getPickupRegionOrder, getRegionName, pickupsData]);

  const applyPickupRegionPrice = useCallback((scheduleId, pickup) => {
    if (!bookingFormData?.date || !excId) return;
    const year = bookingFormData.date.getFullYear();
    const regionId = pickup?.regionId || null;

    if (!regionId) {
      setBookingOptions((prev) => prev.map((o) => (
        Number(o.scheduleId) === Number(scheduleId) ? { ...o, priceRow: o.basePriceRow || o.priceRow } : o
      )));
      return;
    }

    excursionsService
      .getPricelists(year, excId, regionId)
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const row = list.find((r) => Number(r.schedule_id) === Number(scheduleId));
        if (!row) return;
        const adjustedRow = {
          ...row,
          price: row.final_price ?? row.price,
          child_price: row.final_child_price ?? row.child_price,
          infant_price: row.final_infant_price ?? row.infant_price,
        };
        setBookingOptions((prev) => prev.map((o) => (
          Number(o.scheduleId) === Number(scheduleId)
            ? {
                ...o,
                priceRow: {
                  ...adjustedRow,
                  schedule: o.schedule || adjustedRow.schedule || null,
                  minimum_adults: Number(o?.schedule?.minimum_adults ?? adjustedRow?.minimum_adults ?? 1) || 1,
                },
              }
            : o
        )));
      })
      .catch(() => {});
  }, [bookingFormData?.date, excId]);

  useEffect(() => {
    const pending = pendingPickupRef.current;
    if (!pending || !showOptions || !bookingOptions.length) return;
    const groups = getPickupGroupsForSchedule(pending.scheduleId);
    if (!groups.length) return;
    const allPickups = groups.flatMap((g) => g.pickups || []);
    const matched = allPickups.find((p) => {
      const regionOk = !pending.pickupRegion || Number(p?.regionId || 0) === Number(pending.pickupRegion);
      const nameOk = !pending.pickupName || String(p?.accommodationName || '') === String(pending.pickupName);
      const timeOk = !pending.pickupTime || String(p?.pickupTime || '') === String(pending.pickupTime);
      return regionOk && nameOk && timeOk;
    });
    if (matched) {
      setSelectedPickupBySchedule((prev) => ({ ...prev, [pending.scheduleId]: matched }));
      applyPickupRegionPrice(pending.scheduleId, matched);
    }
    pendingPickupRef.current = null;
  }, [applyPickupRegionPrice, bookingOptions, getPickupGroupsForSchedule, showOptions]);

  useEffect(() => {
    if (!showOptions || !Array.isArray(bookingOptions) || bookingOptions.length === 0) return;
    const partnerAccId = Number(partnerAttribution?.accommodation_id || 0) || 0;
    const partnerAccName = String(partnerAttribution?.accommodation_name || '').trim().toLowerCase();
    if (!partnerAccId && !partnerAccName) return;

    const defaultsBySchedule = {};
    bookingOptions.forEach((opt) => {
      const scheduleId = Number(opt?.scheduleId || 0);
      if (!scheduleId) return;
      if (selectedPickupBySchedule[scheduleId]) return;
      const groups = getPickupGroupsForSchedule(scheduleId);
      if (!groups.length) return;
      const allPickups = groups.flatMap((g) => g.pickups || []);
      const byId = partnerAccId
        ? allPickups.find((p) => Number(p?.accId || 0) === partnerAccId)
        : null;
      const byName = !byId && partnerAccName
        ? allPickups.find((p) => String(p?.accommodationName || '').trim().toLowerCase() === partnerAccName)
        : null;
      const matched = byId || byName || null;
      if (matched) defaultsBySchedule[scheduleId] = matched;
    });

    const scheduleIds = Object.keys(defaultsBySchedule);
    if (!scheduleIds.length) return;

    setSelectedPickupBySchedule((prev) => ({ ...prev, ...defaultsBySchedule }));
    scheduleIds.forEach((sid) => {
      const pickup = defaultsBySchedule[sid];
      if (pickup) applyPickupRegionPrice(Number(sid), pickup);
    });
  }, [
    showOptions,
    bookingOptions,
    selectedPickupBySchedule,
    getPickupGroupsForSchedule,
    applyPickupRegionPrice,
    partnerAttribution?.accommodation_id,
    partnerAttribution?.accommodation_name,
  ]);

  useEffect(() => {
    if (!showOptions || !bookingFormData?.date || !excId || bookingOptions.length === 0) return;
    const year = bookingFormData.date.getFullYear();
    const requests = [];
    bookingOptions.forEach((opt) => {
      const pickupGroups = getPickupGroupsForSchedule(opt.scheduleId);
      pickupGroups.forEach((group) => {
        const regionId = Number(group?.regionId || 0);
        if (!regionId) return;
        const key = `${opt.scheduleId}-${regionId}`;
        if (regionPriceCache[key] !== undefined) return;
        requests.push({ key, scheduleId: opt.scheduleId, regionId });
      });
    });
    if (!requests.length) return;

    let cancelled = false;
    Promise.all(
      requests.map(async (req) => {
        try {
          const res = await excursionsService.getPricelists(year, excId, req.regionId);
          const list = res.data?.data ?? res.data ?? [];
          const row = list.find((r) => Number(r.schedule_id) === Number(req.scheduleId));
          if (!row) return { key: req.key, row: null };
          return {
            key: req.key,
            row: {
              ...row,
              price: row.final_price ?? row.price,
              child_price: row.final_child_price ?? row.child_price,
              infant_price: row.final_infant_price ?? row.infant_price,
            },
          };
        } catch {
          return { key: req.key, row: null };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setRegionPriceCache((prev) => {
        const next = { ...prev };
        results.forEach(({ key, row }) => {
          if (next[key] === undefined) next[key] = row;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [bookingFormData?.date, bookingOptions, excId, getPickupGroupsForSchedule, regionPriceCache, showOptions]);

  useEffect(() => {
    if (!showOptions || loadingOptions) return;
    if (bookingOptions.length > 0) {
      setNextAvailableDateSuggestion(null);
      return;
    }
    if (!excId || !bookingFormData?.date || !excursion?.schedules?.length) {
      setNextAvailableDateSuggestion(null);
      return;
    }

    const selected = new Date(bookingFormData.date);
    selected.setHours(0, 0, 0, 0);
    const from = new Date(selected);
    from.setDate(from.getDate() + 1);
    const to = new Date(from);
    to.setDate(to.getDate() + 45);
    const dateFrom = formatYMD(from);
    const dateTo = formatYMD(to);
    let cancelled = false;

    excursionsService.getSalesCapacity({
      date_from: dateFrom,
      date_to: dateTo,
      exc_id: Number(excId || 0),
    })
      .then((capacityRes) => {
        if (cancelled) return;
        const capacityRows = Array.isArray(capacityRes?.data?.data) ? capacityRes.data.data : [];
        const dayStatus = buildDayCapacityStatusMap(
          capacityRows,
          excursion.schedules,
          { languageCode: bookingFormData.language },
        );
        setCapacityStatusByYmd((prev) => {
          const next = new Map(prev);
          dayStatus.forEach((status, ymd) => next.set(ymd, status));
          return next;
        });
        const label = getNextAvailableExcursionDateLabel(
          bookingFormData.date,
          bookingFormData.language,
          excursion.schedules,
          {
            isDateBookable: (ymd) => dayStatus.get(ymd) !== 'unavailable',
          },
        );
        setNextAvailableDateSuggestion(label);
      })
      .catch(() => {
        if (cancelled) return;
        setNextAvailableDateSuggestion(
          getNextAvailableExcursionDateLabel(
            bookingFormData.date,
            bookingFormData.language,
            excursion.schedules,
          ),
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    showOptions,
    loadingOptions,
    bookingOptions.length,
    excId,
    bookingFormData?.date,
    bookingFormData?.language,
    excursion?.schedules,
  ]);

  useEffect(() => {
    if (!openPickupScheduleId) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.pickup-select-wrapper')) {
        setOpenPickupScheduleId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPickupScheduleId]);

  useEffect(() => {
    // Sync booking filters to the address bar without Next.js router navigation.
    // router.replace/push (even with scroll:false) was resetting scroll and remounting
    // widgets when schedule/pickup changed — which felt like a page refresh.
    if (typeof window === 'undefined') return;
    const path = window.location.pathname || '';
    if (/\/(?:checkout|booking)$/.test(path)) return;

    const params = new URLSearchParams();
    if (bookingFormData?.date) params.set('date', formatYMD(bookingFormData.date));
    params.set('adults', String(Number(bookingFormData?.passengers?.adults || 0)));
    params.set('children', String(Number(bookingFormData?.passengers?.children || 0)));
    params.set('infants', String(Number(bookingFormData?.passengers?.infants || 0)));
    if (bookingFormData?.language) params.set('lang', String(bookingFormData.language));

    // Preserve any existing selection params from deep links / back-from-checkout,
    // but do not rewrite them on every option click (that caused the jump).
    const current = new URLSearchParams(window.location.search);
    ['schedule', 'pickup_region', 'pickup_name', 'pickup_time', 'pickup_acc'].forEach((key) => {
      const existing = current.get(key);
      if (existing != null && existing !== '') params.set(key, existing);
    });

    const nextQuery = params.toString();
    const currentQuery = window.location.search.replace(/^\?/, '');
    if (nextQuery === currentQuery || nextQuery === lastSyncedQueryRef.current) {
      return;
    }
    lastSyncedQueryRef.current = nextQuery;
    lastAppliedFilterQueryRef.current = bookingFilterQueryKey(
      bookingFormData?.date ? formatYMD(bookingFormData.date) : '',
      bookingFormData?.passengers,
      bookingFormData?.language,
    );
    const scrollY = window.scrollY;
    const href = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState(window.history.state, '', href);
    window.scrollTo(0, scrollY);
  }, [bookingFormData]);

  return {
    bookingFormData,
    setBookingFormData,
    availabilitySought,
    setAvailabilitySought,
    bookingOptions,
    loadingOptions,
    optionsError,
    selectedOption,
    setSelectedOption,
    selectedPickupBySchedule,
    setSelectedPickupBySchedule,
    regionPriceCache,
    openPickupScheduleId,
    setOpenPickupScheduleId,
    getPickupGroupsForSchedule,
    applyPickupRegionPrice,
    showOptions,
    nextAvailableDateSuggestion,
    capacityStatusByYmd,
    setCapacityStatusByYmd,
  };
}
